import { FC, useEffect } from 'react'
import { addEventListeners, getTimeoutOpenWarningPeriod, getTimeoutWarningDisplayPeriod, removeEventListeners } from '../util/eventListenerUtil'
import { useSelector, useDispatch } from 'react-redux'
import { getUserLoggedInState } from 'redux/reducers/users'
import i18n from 'i18n/config'
import { setSessionWarningModal, getIsSessionWarningModalOpen } from 'redux/reducers/modal'
import { getDefaultLogoutURL } from 'utils/authUrl'
import events, { Event } from 'pubsub/events'
import { LanguagePreference } from 'models/LanguagePreference'
import { createAndEmitLogoutEvent } from 'utils/logoutEvents/create-and-emit-logout-event'

/**
 * A react fragment containing the logic to call
 * window.handleGlobalHeaderTimeout after user inactivity
 */

export const TimeoutLogic: FC = () => {
  const isUserLoggedIn = useSelector(getUserLoggedInState) // TODO - users logged in state matters as it affects what code is executed
  const isSessionWarningModalOpen = useSelector(getIsSessionWarningModalOpen)
  const dispatch = useDispatch()
  const currentLang = i18n.language
  const logoutUrl = getDefaultLogoutURL(currentLang as LanguagePreference)

  useEffect(() => {
    // 3 minute marker after warning has ended
    const createTimeout2 = () => setTimeout(
      () => {
        const timeoutEvent: Event = {
          authenticated: isUserLoggedIn,
          text: '',
          category: '',
          activity: 'System logout',
          url: window.location.href,
          property: 'MAX',
          component: 'Alert Modal',
          title: document.title,
          language: i18n.language,
          modalType: 'Session timeout',
          sendToTealium: false,
          sendToCoreAPI: true,
          isMessageCenter: false
        }
        events.publish(timeoutEvent)
        dispatch(setSessionWarningModal({ isSessionWarningModalOpen: false }))
        if (isUserLoggedIn) {
          createAndEmitLogoutEvent()
          window.location.assign(logoutUrl)
        } else {
          // This is for conditional logic for MCF if it exists. For Unauth Workflows. Auth workflows will just hit the logoutUrl instead
          if (window.handleGlobalHeaderTimeout) {
            window.handleGlobalHeaderTimeout()
          } else {
            // TODO - delete this later, this is for account-settings since we don't have protected routes yet
            window.location.assign(logoutUrl)
          }
        }
      },
      getTimeoutWarningDisplayPeriod() + 1000 // Add one extra second to give the modal transition timer a buffer for countdown
    )

    // 12 minute marker
    const createTimeout = () => setTimeout(
      () => {
        const timeoutEvent: Event = {
          authenticated: isUserLoggedIn,
          text: '',
          category: '',
          activity: 'Alert Modal',
          url: window.location.href,
          property: 'MAX',
          component: 'Alert Modal',
          title: document.title,
          language: i18n.language,
          modalType: 'Session timeout',
          sendToTealium: false,
          sendToCoreAPI: true,
          isMessageCenter: false
        }
        events.publish(timeoutEvent)

        dispatch(setSessionWarningModal({ isSessionWarningModalOpen: true }))

        // If there is another modal, this is logic on medicare-my to unset any active focus traps set
        if (window.handleGlobalHeaderWarningDisplay) {
          window.handleGlobalHeaderWarningDisplay()
        }
      },
      getTimeoutOpenWarningPeriod() - 1000 // Deduct one second to make up for the 1 second added on setTimeout2
    )

    // Function that resets timeout, triggered on every keyboard, mouse, click etc. Gets triggered like 100000xs
    const listener = () => {
      if (!isSessionWarningModalOpen) {
        clearTimeout(timeout)
        timeout = createTimeout()
      }
    }

    let timeout = isSessionWarningModalOpen ? createTimeout2() : createTimeout() // initialize countdown
    addEventListeners(listener) // Adds 6 event listeners to window and makes a callback check for resetting timeout

    // Enables apps to reset timer after it's expired (most likely outside Medicare-My)
    // They'd usually call this in their handleGlobalHeaderTimeout function
    // or as a direct result of their handleGlobalHeaderTimeout function
    // window.resetGlobalHeaderTimeout = (appNameArg?: string) => {
    // window.resetGlobalHeaderTimeout = () => {
    //   console.log('set Timeout')
    //   clearTimeout(timeout)
    //   dispatch(setModal({ isSessionWarningModalOpen: 'none' }))
    //   timeout = createTimeout()
    // }

    return () => {
      removeEventListeners(listener)
      clearTimeout(timeout)
      // window.resetGlobalHeaderTimeout = undefined
    }
  }, [isSessionWarningModalOpen, dispatch, logoutUrl, isUserLoggedIn])
  return <></>
}
